import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { IMAGES, WHATSAPP_URL, WHATSAPP_URL_EN } from "../../lib/content";
import { scrollToId } from "../../lib/scroll";
import { useLang } from "../../lib/i18n";

const lineParent = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.14, delayChildren: 0.5 } },
};
const lineChild = {
  hidden: { y: "115%" },
  visible: {
    y: "0%",
    transition: { duration: 1.1, ease: [0.16, 1, 0.3, 1] },
  },
};

const HeadLine = ({ children }) => (
  <span className="block overflow-hidden">
    <motion.span variants={lineChild} className="block">
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { tr } = useLang();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.55, 0.85]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative h-[100svh] min-h-[640px] w-full overflow-hidden"
    >
      {/* Parallax background */}
      <motion.div style={{ y, scale }} className="absolute inset-0 z-0">
        <img
          src={IMAGES.hero}
          alt="Sposa Atelier Divine"
          className="h-full w-full object-cover object-[center_30%]"
        />
      </motion.div>
      <motion.div
        style={{ opacity: overlayOpacity }}
        className="absolute inset-0 z-[1] bg-[#050505]"
      />
      <div className="absolute inset-0 z-[1] bg-gradient-to-t from-[#050505] via-transparent to-[#050505]/40" />

      {/* Content */}
      <div className="relative z-10 h-full max-w-7xl mx-auto px-6 md:px-10 flex flex-col justify-end pb-20 md:pb-28">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.3 }}
          className="mb-6 flex items-center gap-4"
        >
          <span className="hairline w-14" />
          <span className="font-sans-ui text-[11px] md:text-xs uppercase tracking-[0.4em] text-gold">
            {tr("Alta Moda Sposa · Comiso", "Bridal Haute Couture · Comiso, Sicily")}
          </span>
        </motion.div>

        <motion.h1
          variants={lineParent}
          initial="hidden"
          animate="visible"
          className="font-serif-display font-light text-white leading-[0.92] tracking-tight text-[3.4rem] sm:text-7xl lg:text-8xl xl:text-[8.5rem]"
        >
          <HeadLine>{tr("L'Eleganza", "The Elegance")}</HeadLine>
          <HeadLine>
            {tr("del ", "of ")}<span className="italic text-gold">{tr("Tuo", "Your")}</span>{tr(" Giorno", " Day")}
          </HeadLine>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.3 }}
          className="mt-8 max-w-xl font-sans-ui font-light text-base md:text-lg text-white/75 leading-relaxed"
        >
          {tr(
            "Un atelier sartoriale dove ogni abito nasce su misura. Ti accogliamo su appuntamento per vivere un'esperienza riservata, tra tessuti preziosi e dettagli fatti a mano.",
            "A tailoring atelier where every gown is made to measure. We welcome you by appointment for a private experience among precious fabrics and handmade details."
          )}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.5 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <a
            data-testid="hero-whatsapp-cta"
            href={tr(WHATSAPP_URL, WHATSAPP_URL_EN)}
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex items-center gap-3 bg-[#050505]/40 backdrop-blur-sm border border-gold text-gold px-8 py-4 font-sans-ui text-xs uppercase tracking-[0.25em] hover:bg-gold hover:text-[#050505] transition-colors duration-400"
          >
            {tr("Prenota una Prova", "Book a Fitting")}
            <ArrowDown
              size={15}
              className="-rotate-90 group-hover:translate-x-1 transition-transform duration-300"
            />
          </a>
          <button
            data-testid="hero-scroll-collections"
            onClick={() => scrollToId("#collezioni")}
            className="inline-flex items-center gap-3 text-white/70 px-2 py-4 font-sans-ui text-xs uppercase tracking-[0.25em] hover:text-gold transition-colors duration-300"
          >
            {tr("Scopri le Collezioni", "Discover the Collections")}
          </button>
        </motion.div>
      </div>

      {/* Scroll hint */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        className="absolute bottom-8 left-6 md:left-10 hidden sm:flex flex-col items-center gap-3"
      >
        <span className="font-sans-ui text-[10px] uppercase tracking-[0.3em] text-white/50 [writing-mode:vertical-rl]">
          {tr("Scorri", "Scroll")}
        </span>
        <motion.span
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}
        >
          <ArrowDown size={16} className="text-gold" />
        </motion.span>
      </motion.div>
    </section>
  );
};
